'use client';

import React, { FC, useState } from 'react';
import { TypeGlobalNavFields } from '@/core/generated-types';
import { Image } from '@/components/ui';
import { DynamicComponent } from '@/components/common/DynamicComponent';
import { useCartUrl } from './hooks/useCartUrl';

export const SmallNav: FC<TypeGlobalNavFields> = ({
  logo,
  groups,
  drawerDirection = 'left',
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set());
  const cartHref = useCartUrl();

  const toggleAccordion = (groupId: string) => {
    setOpenAccordions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleMenuClose = () => {
    setIsMenuClosing(true);
    setTimeout(() => {
      setIsMenuOpen(false);
      setIsMenuClosing(false);
    }, 200); // Match animation duration
  };

  return (
    <>
      {/* Nav bar */}
      <nav data-testid="global-nav" className="bg-white md:hidden block px-2">
        <div className="grid grid-cols-[64px_auto_64px] mx-auto h-[64px] items-center">
          {/* Menu */}
          <button
            aria-label="Menu"
            className="p-4"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg
              width="24"
              height="22"
              viewBox="0 0 24 22"
              className="text-charcoal"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="0" y="0" width="24" height="2" fill="currentColor" />
              <rect x="0" y="9" width="24" height="2" fill="currentColor" />
              <rect x="0" y="20" width="24" height="2" fill="currentColor" />
            </svg>
          </button>

          {/* Logo */}
          {logo && (
            <div className="flex items-center justify-center">
              <Image
                imageData={logo}
                alt={logo.fields.altText}
                className="w-[166px]"
              />
            </div>
          )}

          {/* Cart */}
          <a aria-label="Cart" className="p-4" href={cartHref}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="31"
              height="24"
              viewBox="0 0 31 24"
              fill="none"
            >
              <path
                d="M0.890446 0.0012157C0.653314 0.0012157 0.426866 0.0977854 0.259682 0.269197C0.0925044 0.439401 -0.00116472 0.672379 1.09348e-05 0.912583C0.00120772 1.41354 0.399588 1.81913 0.890455 1.82034H3.43488C4.16523 1.82034 4.79366 2.3068 4.99047 3.02021L5.23827 3.91709V3.9183V3.92071L8.9908 17.5202C9.27773 18.5607 10.0223 19.3767 10.9626 19.7751C10.7527 20.1759 10.633 20.6333 10.633 21.115C10.633 22.6975 11.9147 24 13.4714 24C15.0282 24 16.3099 22.6987 16.3099 21.115C16.3099 20.7371 16.2364 20.375 16.1036 20.0431H19.4543C19.3215 20.375 19.2468 20.7371 19.2468 21.115C19.2468 22.6975 20.5332 24 22.0912 24C23.648 24 24.9297 22.6987 24.9297 21.115C24.9297 20.7371 24.8562 20.375 24.7234 20.0431H26.2291C26.72 20.0406 27.116 19.6363 27.1184 19.1377C27.1172 18.6368 26.72 18.2324 26.2291 18.23H12.2717C11.5413 18.23 10.9058 17.7435 10.709 17.0301L10.6189 16.7017H23.9954C25.7122 16.7017 27.2228 15.5224 27.672 13.8361L30.3208 3.91951V3.92071C30.3943 3.6467 30.3386 3.35338 30.1691 3.12884C29.9995 2.90311 29.7375 2.77155 29.4576 2.77155H6.7749L6.70851 2.53013C6.29707 1.03934 4.95611 0 3.43373 0L0.890446 0.0012157ZM7.27761 4.59175H28.2922L25.9517 13.359C25.711 14.2608 24.9166 14.8824 23.9953 14.8824H10.1159L7.27761 4.59175ZM13.4714 20.0429C14.0631 20.0429 14.5207 20.5137 14.5207 21.1148C14.5207 21.716 14.0631 22.1819 13.4714 22.1819C12.8798 22.1819 12.4162 21.716 12.4162 21.1148C12.4162 20.5137 12.8798 20.0429 13.4714 20.0429ZM22.0923 20.0429C22.6839 20.0429 23.1416 20.5137 23.1416 21.1148C23.1416 21.716 22.6839 22.1819 22.0923 22.1819C21.5006 22.1819 21.037 21.716 21.037 21.1148C21.037 20.5137 21.5006 20.0429 22.0923 20.0429Z"
                fill="#424140"
              />
            </svg>
          </a>
        </div>
      </nav>

      {/* Menu */}
      {isMenuOpen && (
        <div
          className={`bg-white border-t border-grey-mid-light md:hidden fixed top-0 bottom-0 w-full transform transition-all duration-200 ease-in-out ${
            drawerDirection === 'left'
              ? `left-0 ${isMenuClosing ? 'animate-slide-out-left' : 'animate-slide-in-left'}`
              : `right-0 ${isMenuClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`
          }`}
        >
          <div className="flex justify-end pb-8">
            <button
              aria-label="Close"
              onClick={handleMenuClose}
              className="p-5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
              >
                <path
                  d="M11 12.827L2.20416 21.6233C1.96402 21.8632 1.66219 21.986 1.29866 21.9918C0.935414 21.9973 0.628088 21.8744 0.37668 21.6233C0.12556 21.3719 0 21.0673 0 20.7096C0 20.3518 0.12556 20.0472 0.37668 19.7958L9.17295 11L0.37668 2.20416C0.13683 1.96402 0.0140154 1.66219 0.00823591 1.29866C0.00274537 0.935415 0.12556 0.628088 0.37668 0.37668C0.628088 0.12556 0.932669 0 1.29042 0C1.64817 0 1.95275 0.12556 2.20416 0.37668L11 9.17295L19.7958 0.37668C20.036 0.13683 20.3378 0.0140154 20.7013 0.00823591C21.0646 0.00274537 21.3719 0.12556 21.6233 0.37668C21.8744 0.628088 22 0.932669 22 1.29042C22 1.64817 21.8744 1.95275 21.6233 2.20416L12.827 11L21.6233 19.7958C21.8632 20.036 21.986 20.3378 21.9918 20.7013C21.9973 21.0646 21.8744 21.3719 21.6233 21.6233C21.3719 21.8744 21.0673 22 20.7096 22C20.3518 22 20.0472 21.8744 19.7958 21.6233L11 12.827Z"
                  fill="#424140"
                />
              </svg>
            </button>
          </div>
          <div className="px-4 max-h-[80vh] overflow-y-auto">
            {groups?.map((group) => (
              <div
                key={group.sys.id}
                className="border-b border-transparent last:border-b-0"
              >
                <button
                  className="w-full py-4 text-left flex items-center justify-between text-lg text-charcoal pr-1.5"
                  onClick={() => toggleAccordion(group.sys.id)}
                  aria-expanded={openAccordions.has(group.sys.id)}
                >
                  <span>{group.fields.title}</span>
                  <svg
                    className={`w-5 h-5 transform transition-transform duration-200 ${
                      openAccordions.has(group.sys.id) ? 'rotate-180' : ''
                    }`}
                    xmlns="http://www.w3.org/2000/svg"
                    width="21"
                    height="13"
                    viewBox="0 0 21 13"
                    fill="none"
                  >
                    <path
                      d="M19.75 11.125L10.5 1.875L1.25 11.125"
                      stroke="#424140"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openAccordions.has(group.sys.id)
                      ? 'max-h-[1000px] opacity-100'
                      : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pl-[21px] flex flex-col">
                    {group.fields.columns?.map((column) =>
                      column.fields.items?.map((item) => (
                        <DynamicComponent component={item} key={item.sys.id} />
                      )),
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(SmallNav);
